import { useEffect, useMemo, useState } from "react";
import { useSpreadsStore } from "@/stores/spreads";
import { ALL_EXCHANGES, isBlocked } from "@/pages/Spreads/pipeline";
import { spreadUid, type Spread } from "@/types/spread";

/**
 * Live stats for the TopBar ribbon — derived entirely from the
 * existing ``useSpreadsStore`` snapshot. No new endpoint, no extra
 * polling: piggy-backs on whatever the spreads page (or any other
 * page that touches ``useSpreadStream``) is already pushing into
 * the store.
 *
 * Returns:
 *   * ``activeCount`` — number of currently-visible spread rows
 *   * ``topSpread``   — the row with the highest ``net_spread_pct``
 *   * ``ageS``        — seconds since the last analyzer snapshot,
 *                       ticking once per second; ``null`` until the
 *                       first snapshot arrives
 *   * ``connected``   — websocket alive flag (drives the live dot)
 *
 * Why a hook (and not selectors inline in TopBar)?
 *   1. ageS needs its own ``setInterval`` tick — colocate the timer
 *      logic with the value so callers don't re-implement it.
 *   2. ``topSpread`` is an O(N) scan of the rows Map; ``useMemo``
 *      against ``rows.size + lastSnapshotAt`` keeps the cost in the
 *      hook rather than re-running on every consumer render.
 */
/** Minimum executable size (USD) for a spread to be eligible as the ribbon's
 *  headline "TOP" — below this it's a thin-book phantom, not an opportunity. */
const MIN_TOP_VOLUME_USD = 200;

export function useTopBarStats() {
  const rows = useSpreadsStore((s) => s.rows);
  const meta = useSpreadsStore((s) => s.meta);
  const lastSnapshotAt = useSpreadsStore((s) => s.lastSnapshotAt);
  const connected = useSpreadsStore((s) => s.connected);

  // Top spread by net_spread_pct. The store's Map preserves insertion
  // order, not sort order — and ``SpreadTable`` does its own per-page
  // sort — so we have to scan. ``rows.size + lastSnapshotAt`` is the
  // cheapest invalidation key (both change on every snapshot).
  const topSpread: Spread | null = useMemo(() => {
    if (rows.size === 0) return null;
    let best: Spread | null = null;
    let bestPct = -Infinity;
    for (const r of rows.values()) {
      // Match the default Spreads table (hideBlocked=true "Actionable" +
      // crypto-only): skip blocked rows (D/W rails closed) and TradFi rows so
      // the ribbon never advertises a "top" the user can't find in the table
      // (e.g. a blocked micro-cap phantom like BITBEDR @ 41140%).
      if (r.tradfi_group) continue;
      if (isBlocked(r)) continue;
      // Skip untradeable thin-book phantoms: a micro-cap with near-zero volume can
      // show a 5-digit % the trader can't act on (audit 2026-08-24 — the ribbon
      // headlined "TOP +29286% ON"). Only advertise a spread with real executable
      // size at top-of-book.
      if (!(Number(r.volume_usd) >= MIN_TOP_VOLUME_USD)) continue;
      // Skip ghost rows — a spread that vanished from the latest snapshot
      // lingers in the store for GHOST_TTL carrying its LAST net_spread_pct.
      // Without this the ribbon kept advertising a just-closed "top" (e.g.
      // "NODE 14.81%") that the live table no longer lists as an active row.
      if (meta.get(spreadUid(r))?.closedAt != null) continue;
      const pct = Number(r.net_spread_pct);
      if (Number.isFinite(pct) && pct > bestPct) {
        bestPct = pct;
        best = r;
      }
    }
    return best;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, rows.size, lastSnapshotAt, meta]);

  // Tick once per second so the "Ns ago" reading stays fresh. We can't
  // derive this from ``lastSnapshotAt`` alone because snapshots arrive
  // every ~500-2000 ms and we'd want to show 1-2 s freshness without
  // re-rendering the whole spreads page every tick.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ageS: number | null = lastSnapshotAt
    ? Math.max(0, Math.floor((now - lastSnapshotAt) / 1000))
    : null;

  return {
    activeCount: rows.size,
    topSpread,
    ageS,
    connected,
  };
}

/** Set form of the CEX roster for O(1) membership checks. */
const KNOWN_VENUES = new Set<string>(ALL_EXCHANGES);

/**
 * Distinct count of known CEX venues currently present in the live
 * spreads snapshot, plus the websocket ``connected`` flag. Drives the
 * sidebar's "Connected · N exchanges" status (previously a hard-coded
 * "12" that never reflected reality). Intersecting with ``ALL_EXCHANGES``
 * keeps DEX-chain legs or stray symbols from inflating the number.
 * Memoised on snapshot identity so it only recomputes when a new
 * snapshot lands and re-renders only when the integer actually changes.
 */
export function useConnectedVenues(): { count: number; connected: boolean } {
  const rows = useSpreadsStore((s) => s.rows);
  const lastSnapshotAt = useSpreadsStore((s) => s.lastSnapshotAt);
  const connected = useSpreadsStore((s) => s.connected);

  const count = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows.values()) {
      if (KNOWN_VENUES.has(r.buy_exchange)) seen.add(r.buy_exchange);
      if (KNOWN_VENUES.has(r.sell_exchange)) seen.add(r.sell_exchange);
    }
    return seen.size;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, rows.size, lastSnapshotAt]);

  return { count, connected };
}
